/* sugar:assets/yotpo-reviews.js@2.0.0 */
/* The server draws the list in its starting order with page 1 showing, so nothing here moves on load. */
(() => {
  if (customElements.get('sugar-yotpo-reviews')) return;
  class SugarYotpoReviews extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.page = 1;
      this.topic = '';
      this.rows = [...this.querySelectorAll('[data-review-id]')];
      this.rows.forEach((row) => {
        row._search = [...row.querySelectorAll('[data-search-text]')]
          .map((el) => el.textContent)
          .join(' ')
          .toLowerCase();
        row._tags = (row.dataset.tags || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      });
      this.pageSize = Math.max(1, Number(this.dataset.pageSize) || 5);
      // Load more keeps every earlier page on screen and adds the next one below it.
      this.loadMore = this.dataset.paging === 'load_more';
      const on = (type, fn) => this.addEventListener(type, fn, { signal });
      on('input', (e) => {
        if (e.target.matches('[data-search]')) {
          this.page = 1;
          this.render();
        }
      });
      on('change', (e) => {
        if (e.target.matches('[data-rating],[data-country],[data-type],[data-sort]')) {
          this.page = 1;
          this.render();
        }
      });
      on('click', (e) => {
        const button = e.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.hasAttribute('data-topic')) {
          this.topic = this.topic === button.dataset.topic ? '' : button.dataset.topic;
          this.page = 1;
          this.render();
        }
        if (button.hasAttribute('data-page')) {
          this.page = Number(button.dataset.page);
          this.render();
          this.querySelector('[data-review-list]').scrollIntoView({
            behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'start',
          });
        }
        if (button.hasAttribute('data-load-more')) this.more(button);
        if (button.hasAttribute('data-reset')) {
          this.topic = '';
          this.page = 1;
          this.fields().forEach((field) => (field.value = ''));
          this.render();
        }
        if (button.hasAttribute('data-more-topics')) {
          const expanded = this.querySelector('.sugar-yotpo-reviews__topics').classList.toggle('is-expanded');
          button.setAttribute('aria-expanded', String(expanded));
        }
        if (button.hasAttribute('data-open-summary')) {
          this.summaryTrigger = button;
          this.querySelector('dialog').showModal();
        }
        if (button.hasAttribute('data-close-summary')) this.querySelector('dialog').close();
        if (button.hasAttribute('data-vote')) this.vote(button);
      });
      const dialog = this.querySelector('dialog');
      if (dialog) {
        dialog.addEventListener(
          'click',
          (e) => {
            if (e.target !== dialog) return;
            const r = dialog.getBoundingClientRect();
            if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dialog.close();
          },
          { signal },
        );
        dialog.addEventListener('close', () => this.summaryTrigger?.focus(), { signal });
      }
      document.addEventListener(
        'shopify:block:select',
        (e) => {
          const row = this.rows.find((r) => r.dataset.reviewId === e.detail.blockId);
          if (!row) return;
          this.topic = '';
          this.fields().forEach((field) => (field.value = ''));
          this.render(row);
        },
        { signal },
      );
      this.restoreVotes();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }

    fields() {
      return [...this.querySelectorAll('[data-search],[data-rating],[data-country],[data-type]')];
    }

    value(selector) {
      const field = this.querySelector(selector);
      return field ? field.value : '';
    }

    text(name, values) {
      let value = this.dataset[name] || '';
      Object.keys(values).forEach((key) => {
        value = value.split(`[${key}]`).join(values[key]);
      });
      return value;
    }

    filtered() {
      const search = this.value('[data-search]').trim().toLowerCase();
      const rating = this.value('[data-rating]');
      const country = this.value('[data-country]');
      const type = this.value('[data-type]');
      const sort = this.value('[data-sort]') || 'merchant';
      const topic = this.topic.toLowerCase();
      const list = this.rows.filter(
        (row) =>
          (!search || row._search.includes(search)) &&
          (!rating || row.dataset.rating === rating) &&
          (!country || row.dataset.country === country) &&
          (!topic || row._tags.includes(topic)) &&
          (type !== 'verified' || row.dataset.verified === 'true') &&
          (type !== 'media' || row.dataset.media === 'true'),
      );
      const index = (a, b) => Number(a.dataset.index) - Number(b.dataset.index);
      return list.sort((a, b) => {
        if (sort === 'recent') return (Date.parse(b.dataset.date) || 0) - (Date.parse(a.dataset.date) || 0) || index(a, b);
        if (sort === 'highest') return Number(b.dataset.rating) - Number(a.dataset.rating) || index(a, b);
        if (sort === 'lowest') return Number(a.dataset.rating) - Number(b.dataset.rating) || index(a, b);
        if (sort === 'helpful') return Number(b.dataset.votes) - Number(a.dataset.votes) || index(a, b);
        return index(a, b);
      });
    }

    render(selectedRow) {
      const rows = this.filtered();
      const count = Math.ceil(rows.length / this.pageSize);
      if (selectedRow && rows.includes(selectedRow)) this.page = Math.floor(rows.indexOf(selectedRow) / this.pageSize) + 1;
      this.page = Math.max(1, Math.min(this.page, count || 1));
      const visible = new Set(rows.slice(this.loadMore ? 0 : (this.page - 1) * this.pageSize, this.page * this.pageSize));
      const list = this.querySelector('[data-review-list]');
      this.rows.forEach((row) => {
        row.hidden = !visible.has(row);
      });
      rows.forEach((row) => list.appendChild(row));
      this.querySelector('[data-empty]').hidden = rows.length > 0;
      this.querySelector('[data-reset]').hidden = !this.topic && !this.fields().some((field) => field.value);
      this.querySelectorAll('[data-topic]').forEach((button) =>
        button.setAttribute('aria-pressed', String(button.dataset.topic === this.topic)),
      );
      const more = this.querySelector('[data-load-more]');
      if (more) more.hidden = this.page >= count;
      const nav = this.querySelector('[data-pagination]');
      if (nav) {
        nav.replaceChildren();
        const pages = new Set([1, count, this.page - 1, this.page, this.page + 1].filter((p) => p > 0 && p <= count));
        let previous = 0;
        [...pages]
          .sort((a, b) => a - b)
          .forEach((page) => {
            if (previous && page - previous > 1) {
              const dots = document.createElement('span');
              dots.textContent = '…';
              nav.appendChild(dots);
            }
            const button = document.createElement('button');
            button.type = 'button';
            button.dataset.page = page;
            button.textContent = page;
            button.setAttribute('aria-label', this.text('strPage', { page }));
            if (page === this.page) button.setAttribute('aria-current', 'page');
            nav.appendChild(button);
            previous = page;
          });
        nav.hidden = count <= 1;
      }
      this.querySelector('[data-results]').textContent = this.text('strResults', {
        count: rows.length,
        page: this.page,
        pages: Math.max(1, count),
      });
      return rows;
    }

    more(button) {
      const start = this.page * this.pageSize;
      this.page += 1;
      const rows = this.render();
      const added = rows.slice(start, this.page * this.pageSize);
      if (!added.length) return;
      this.querySelector('[data-results]').textContent = this.text('strMore', { count: added.length });
      // The button leaves with the last page, so focus moves on to the first review it added.
      if (button.hidden) {
        added[0].tabIndex = -1;
        added[0].focus({ preventScroll: true });
      }
    }

    storageKey(row) {
      return `sugar-reviews:${this.dataset.sectionId}:${row.dataset.reviewId}`;
    }

    restoreVotes() {
      this.rows.forEach((row) => {
        row
          .querySelectorAll('[data-vote]')
          .forEach((b) => (b.dataset.baseCount = b.querySelector('[data-vote-count]').textContent));
        try {
          const vote = localStorage.getItem(this.storageKey(row));
          if (vote === 'up' || vote === 'down') this.paintVote(row, vote);
        } catch {}
      });
    }

    paintVote(row, vote) {
      row.querySelectorAll('[data-vote]').forEach((b) => {
        const active = b.dataset.vote === vote;
        b.setAttribute('aria-pressed', String(active));
        b.querySelector('[data-vote-count]').textContent = Math.max(0, Number(b.dataset.baseCount) || 0) + (active ? 1 : 0);
      });
      row.dataset.votes = row.querySelector('[data-vote=up] [data-vote-count]').textContent;
    }

    vote(button) {
      const row = button.closest('[data-review-id]');
      const previous = row.querySelector('[data-vote][aria-pressed="true"]');
      const vote = button.getAttribute('aria-pressed') === 'true' ? '' : button.dataset.vote;
      this.paintVote(row, vote);
      try {
        if (vote) localStorage.setItem(this.storageKey(row), vote);
        else localStorage.removeItem(this.storageKey(row));
      } catch {}
      this.dispatchEvent(
        new CustomEvent('sugar:reviews:vote', {
          bubbles: true,
          detail: {
            sectionId: this.dataset.sectionId,
            reviewId: row.dataset.reviewId,
            vote: vote || null,
            previous: previous ? previous.dataset.vote : null,
          },
        }),
      );
    }
  }
  customElements.define('sugar-yotpo-reviews', SugarYotpoReviews);
})();
